import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import currencies from './curruncies.json';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/material';
// components
import TextMaxLine from '../../../components/TextMaxLine';
import { FormProvider, RHFSelect, RHFTextField } from '../../../components/hook-form';
import axiosInstance from 'src/utils/axios';
import { Package } from 'src/@types/package';
import { APIS } from 'src/utils/serviceUrls';

// ----------------------------------------------------------------------


type Props = {
  isEdit: boolean;
  currentPackage?: Package;
  handleClose(): void;
};

export default function PackageNewEditForm({ isEdit, currentPackage, handleClose }: Props) {
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();
  const expression = /^(ftp|http|https):\/\/[^ "]+$/;


  const NewPackageSchema = Yup.object().shape({
    name: Yup.string().min(3).required('package name is required'),
    cost: Yup.number().required('cost is required'),
    // Empty -> null: the backend then uses its default EGP table.
    price_egp: Yup.number()
      .nullable()
      .transform((value, original) => (original === '' || original === null || Number.isNaN(value) ? null : value))
      .min(0, 'price must be 0 or more'),
    currency: Yup.string().min(3).required('currency is required'),
    duration: Yup.number().min(30).required('duration is Required'),
    description: Yup.string().min(3).required('description is Required'),
    // icon: Yup.string().required('icon is Required'),
    icon: Yup.string().required('icon is Required').matches(expression, 'Icon Url must be a valid url'),
  });

  const defaultValues: Package = useMemo(
    () => ({
      name: currentPackage?.name || '',
      cost: currentPackage?.cost || 0,
      price_egp: currentPackage?.price_egp ?? null,
      currency: currentPackage?.currency || '',
      duration: currentPackage?.duration || 0,
      description: currentPackage?.description || "",
      icon: currentPackage?.icon || "",
    }),
    [currentPackage]
  );

  const methods = useForm<Package>({
    resolver: yupResolver(NewPackageSchema),
    defaultValues,
  });

  const {
    reset,
    watch,setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && currentPackage) {
      reset(currentPackage);
    }
    if (!isEdit) {
      reset(currentPackage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, currentPackage]);

  const onSubmit = async (data: Package) => {
    if (!data?.name_ar)
      delete data?.name_ar
    try {
      if (currentPackage)
        await axiosInstance.patch(APIS.PACKAGES.PATCH(currentPackage._id as string), data);
      else
         await axiosInstance.post(APIS.PACKAGES.ADD, data);
      reset();
      enqueueSnackbar(!isEdit ? 'Create success!' : 'Update success!');
      handleClose();
    } catch (error) {
      console.error(error);
    }
  };



  return (
    <Stack height={'100%'}>
      <FormProvider  methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <TextMaxLine marginBottom={'20px'} marginTop={'20px'} fontSize={'22px'}>
          {isEdit ? ' Edit Package' : ' Create Package'}
        </TextMaxLine>
        <Stack direction={'column'} sx={{ overflow:'auto' }} justifyContent={'space-between'} height={'90%'}>
          <Stack direction={'column'} spacing={5}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 5,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="name" label="Package English Name" />
              <RHFTextField name="name_ar" label="Package Arabic Name" />
              <div style={{ width: '100%', position: 'relative' }}>
                <RHFSelect name="currency" label="currency">
                  <option key={'default'} disabled={true} value={''}>
                    {''}
                  </option>
                  {Object.keys(currencies).map((currency, index) => (
                    <option value={currency} key={index}>
                      {currency}
                    </option>
                  ))}
                </RHFSelect>
              </div>
              <RHFTextField type={'number'} name="cost" label="Fees" />
              <RHFTextField
                type={'number'}
                name="price_egp"
                label="Checkout price (EGP)"
                helperText="What every buyer pays at checkout. Leave empty for the default (Monthly 899, Bootcamp 10,000, others 15,000)."
              />
            </Box>
              <RHFTextField type={'number'} name="duration" label="Duration in days " />
            <RHFTextField name="icon" label="Icon Image Url" />

            <RHFTextField
              multiline
              minRows={2}
              fullWidth
              name="description"
              label="English Description"
            />
            <RHFTextField
              multiline
              minRows={2}
              fullWidth
              name="description_ar"
              label="Arabic Description"
            />
          </Stack>

          <Stack alignItems="flex-end" sx={{ mt: 3 }}>
            <LoadingButton
              style={{ width: '200px', height: '50px' }}
              type="submit"
              variant="contained"
              loading={isSubmitting}
            >
              {!isEdit ? 'Create Package' : 'Save Changes'}
            </LoadingButton>
          </Stack>
        </Stack>
      </FormProvider>
    </Stack>
  );
}
